import { Suspense, type ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { useModules } from '@/contexts/ModuleContext';
import { PageSkeleton } from './PageSkeleton';

interface ModuleGateProps {
  /** The module slug that must be enabled (e.g. 'crm', 'inventory') */
  module: string;
  children: ReactNode;
}

/**
 * Route guard that prevents rendering (and downloading) of module chunks
 * when the tenant does not have the module enabled.
 *
 * Wrap lazy-loaded route elements with this:
 * ```tsx
 * <Route path="crm" element={
 *   <ModuleGate module="crm"><CrmDashboardPage /></ModuleGate>
 * } />
 * ```
 */
export function ModuleGate({ module, children }: ModuleGateProps) {
  const { hasModule, isLoading } = useModules();

  if (isLoading) return <PageSkeleton />;
  if (!hasModule(module)) return <Navigate to="/dashboard" replace />;

  return <Suspense fallback={<PageSkeleton />}>{children}</Suspense>;
}
